import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Thermometer, Snowflake, Droplets, AlertTriangle, CheckCircle2, Sun, Moon } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const inputCls =
  "rounded-none border-[#D4D4D4] dark:border-[#1F1F1F] dark:bg-[#0A0A0A] dark:text-white focus-visible:ring-0 focus-visible:border-[#0A0A0A] dark:focus-visible:border-[var(--neon-cyan)] h-9 font-mono text-sm";

const TS825_U_MAX = { 1: 0.66, 2: 0.57, 3: 0.48, 4: 0.38, 5: 0.36 };

function gradeU(u, zone, t) {
  const target = TS825_U_MAX[zone] ?? 0.57;
  if (u <= target * 0.7) return { color: "#34C759", label: t("metrics.gradeExcellent", "EXCELLENT") };
  if (u <= target) return { color: "#34C759", label: `TS 825 ${t("metrics.gradePass", "PASS")} (≤${target})` };
  if (u <= target * 1.3) return { color: "#FFCC00", label: `${t("metrics.gradeBorderline", "BORDERLINE")} (${target})` };
  return { color: "#FF3B30", label: `${t("metrics.gradeFail", "FAIL")} (${target})` };
}

export default function MetricsPanel({ metrics, conditions, setConditions, zone = 3 }) {
  const { t } = useI18n();
  if (!metrics) return null;
  const grade = gradeU(metrics.u_value, zone, t);

  return (
    <div data-testid="metrics-panel">
      <div
        className="mb-3"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: "6px",
          padding: 0,
        }}>
        <h2
          className="text-lg font-semibold tracking-tight"
          style={{ margin: 0, padding: 0, textAlign: "left" }}
          data-testid="metrics-title">
          {t("metrics.title", "Thermal Performance")}
        </h2>
        <span
          className="tech-label"
          style={{ margin: 0, padding: 0, textAlign: "left" }}
          data-testid="metrics-realtime">
          {t("metrics.realtime", "REAL-TIME · TS 825:2024")}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-px bg-[#D4D4D4] dark:bg-[#1F1F1F] border border-[#D4D4D4] dark:border-[#1F1F1F]">
        <div className="bg-white dark:bg-[#0A0A0A] p-4">
          <div className="tech-label mb-2">{t("metrics.totalR", "TOTAL R-VALUE")}</div>
          <div className="font-mono text-3xl font-semibold neon-cyan" data-testid="r-value">
            {metrics.total_r.toFixed(3)}
          </div>
          <div className="tech-label mt-1">M²K/W</div>
        </div>
        <div className="bg-white dark:bg-[#0A0A0A] p-4">
          <div className="tech-label mb-2">{t("metrics.uValue", "U-VALUE")}</div>
          <div className="font-mono text-3xl font-semibold" style={{ color: "#00BFFF", textShadow: "0 0 8px rgba(0,191,255,0.55)" }} data-testid="u-value">
            {metrics.u_value.toFixed(3)}
          </div>
          <div className="tech-label mt-1">W/M²K</div>
        </div>
      </div>
      <div className="border-x border-b border-[#D4D4D4] dark:border-[#1F1F1F] bg-white dark:bg-[#0A0A0A] px-4 py-2 flex items-center gap-2">
        <span className="w-2 h-2" style={{ background: grade.color }}/>
        <span className="font-mono text-xs uppercase tracking-wider" data-testid="u-grade">{grade.label}</span>
      </div>

      {/* Gündüz / Gece toggle moved to header — no inline duplicate */}

      <div className="grid grid-cols-4 gap-2 mt-4 items-end">
        <div className="flex flex-col justify-end">
          <Label className="tech-label flex items-center gap-1 whitespace-nowrap mb-1"><Thermometer className="w-3 h-3"/>{t("metrics.indoorT", "INDOOR °C")}</Label>
          <Input type="number" step="0.5" className={inputCls} value={conditions.indoor_temp}
            onChange={e => setConditions({ ...conditions, indoor_temp: Number(e.target.value) })} data-testid="indoor-temp"/>
        </div>
        <div className="flex flex-col justify-end">
          <Label className="tech-label flex items-center gap-1 whitespace-nowrap mb-1"><Snowflake className="w-3 h-3"/>{t("metrics.outdoorT", "OUTDOOR °C")}</Label>
          <Input type="number" step="0.5" className={inputCls} value={conditions.outdoor_temp}
            onChange={e => setConditions({ ...conditions, outdoor_temp: Number(e.target.value) })} data-testid="outdoor-temp"/>
        </div>
        <div className="flex flex-col justify-end">
          <Label className="tech-label flex items-center gap-1 whitespace-nowrap mb-1"><Droplets className="w-3 h-3"/>{t("metrics.indoorRH", "INDOOR %")}</Label>
          <Input type="number" step="1" className={inputCls} value={conditions.indoor_humidity}
            onChange={e => setConditions({ ...conditions, indoor_humidity: Number(e.target.value) })} data-testid="indoor-humidity"/>
        </div>
        <div className="flex flex-col justify-end">
          <Label className="tech-label flex items-center gap-1 whitespace-nowrap mb-1"><Droplets className="w-3 h-3"/>{t("metrics.outdoorRH", "OUTDOOR %")}</Label>
          <Input type="number" step="1" className={inputCls} value={conditions.outdoor_humidity ?? 80}
            onChange={e => setConditions({ ...conditions, outdoor_humidity: Number(e.target.value) })} data-testid="outdoor-humidity"/>
        </div>
      </div>

      <div className={`mt-4 border p-3 flex items-start gap-3 ${
        metrics.condensation_risk ? "border-[#FF3B30] bg-[#FFF5F4] dark:bg-[#2A0F0F]" : "border-[#34C759] bg-[#F0FFF4] dark:bg-[#0F2A14]"
      }`} data-testid="condensation-status">
        {metrics.condensation_risk
          ? <AlertTriangle className="w-5 h-5 text-[#FF3B30] mt-0.5"/>
          : <CheckCircle2 className="w-5 h-5 text-[#34C759] mt-0.5"/>}
        <div>
          <div className="text-sm font-semibold">
            {metrics.condensation_risk
              ? t("metrics.condensationRisk", "CONDENSATION RISK")
              : t("metrics.noCondensation", "NO CONDENSATION RISK")}
          </div>
          <div className="font-mono text-xs text-[#52525B] mt-1">
            {t("calc.condensation.surface", "Surface")}: {metrics.surface_condensation_risk
              ? t("calc.condensation.risky", "RISK")
              : t("calc.condensation.ok", "OK")} ·
            {" "}{t("calc.condensation.layer", "Interlayer")}: {metrics.interstitial_condensation_risk
              ? t("calc.condensation.risky", "RISK")
              : t("calc.condensation.ok", "OK")} ·
            {" "}{t("calc.condensation.dew", "Dew")}: {metrics.dew_point.toFixed(1)}°C ·
            {" "}{t("calc.condensation.inner", "Inner surface")}: {metrics.interior_surface_temp.toFixed(1)}°C
          </div>
        </div>
      </div>
    </div>
  );
}
